import { Redirect } from 'expo-router';
import {
  BarChart3,
  Bell,
  LayoutDashboard,
  Phone,
  Search,
  Settings,
  Users,
} from 'lucide-react-native';
import { type ReactNode, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { ShimmerSkeleton, SkeletonCard } from '../../src/components/animations/Skeleton';
import {
  BottomNav,
  type BottomNavTab,
  Box,
  Card,
  ChecklistItem,
  Header,
  HeaderAvatar,
  HStack,
  Input,
  MetricCard,
  MutedBadge,
  OverviewBadge,
  SearchInput,
  Select,
  StatusBadge,
  StatusBanner,
  Text,
  VStack,
} from '../../src/components/primitives';
import { colors } from '../../src/theme/colors';
import { tokens } from '../../src/theme/tokens';
import { darkPrecisionTypography } from '../../src/theme/typography';

export default function DesignSystemScreen() {
  const [checks, setChecks] = useState<Record<string, boolean>>({
    profile: true,
    leads: false,
    voice: false,
    invite: false,
  });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('qualified');

  if (!__DEV__) {
    return <Redirect href="/" />;
  }

  const toggle = (key: string) => setChecks((prev) => ({ ...prev, [key]: !prev[key] }));

  const navTabs: BottomNavTab[] = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      onPress: () => setActiveTab('dashboard'),
    },
    { key: 'leads', label: 'Leads', icon: Users, onPress: () => setActiveTab('leads') },
    { key: 'calls', label: 'Calls', icon: Phone, onPress: () => setActiveTab('calls') },
    {
      key: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      onPress: () => setActiveTab('analytics'),
    },
    { key: 'settings', label: 'Settings', icon: Settings, onPress: () => setActiveTab('settings') },
  ];

  return (
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      <Text style={[darkPrecisionTypography.label, styles.eyebrow]}>
        Dark Precision · Primitives
      </Text>
      <Text style={[darkPrecisionTypography.displayLg, styles.heading]}>FLOSEAI-215 showcase</Text>

      <Section title="Card variants">
        <VStack spacing="md">
          <Card variant="default">
            <Text style={darkPrecisionTypography.heading}>Default card</Text>
            <Text style={darkPrecisionTypography.bodyMuted}>Subtle border, no glow.</Text>
          </Card>
          <Card variant="elevated">
            <Text style={darkPrecisionTypography.heading}>Elevated card</Text>
            <Text style={darkPrecisionTypography.bodyMuted}>
              Ambient shadow + faint gradient on web.
            </Text>
          </Card>
          <Card variant="accent">
            <Text style={darkPrecisionTypography.heading}>Accent card</Text>
            <Text style={darkPrecisionTypography.bodyMuted}>Teal halo + inset accent border.</Text>
          </Card>
        </VStack>
      </Section>

      <Section title="Badge presets">
        <HStack spacing="sm" align="center" style={styles.row}>
          <OverviewBadge>Overview</OverviewBadge>
          <MutedBadge>Muted</MutedBadge>
          <StatusBadge status="success">Active</StatusBadge>
          <StatusBadge status="warning">Pending</StatusBadge>
          <StatusBadge status="danger">Failed</StatusBadge>
        </HStack>
      </Section>

      <Section title="MetricCard (animated)">
        <VStack spacing="md">
          <MetricCard label="Leads today" value={142} subtext="+12% vs yesterday" index={0} />
          <MetricCard
            label="Conversion"
            value={8.42}
            suffix="%"
            decimals={2}
            subtext="rolling 7d"
            index={1}
            active
          />
          <MetricCard label="Pipeline value" value={48720} prefix="$" subtext="open" index={2} />
          <MetricCard label="Coming soon" value={null} index={3} />
        </VStack>
      </Section>

      <Section title="ChecklistItem">
        <Card variant="default" padding="md">
          <VStack spacing="xs">
            <ChecklistItem
              label="Complete your profile"
              checked={checks.profile}
              onPress={() => toggle('profile')}
              onCtaPress={() => toggle('profile')}
            />
            <ChecklistItem
              label="Import your first leads"
              checked={checks.leads}
              onPress={() => toggle('leads')}
              onCtaPress={() => toggle('leads')}
            />
            <ChecklistItem
              label="Configure a voice agent"
              checked={checks.voice}
              onPress={() => toggle('voice')}
              onCtaPress={() => toggle('voice')}
            />
            <ChecklistItem
              label="Invite a teammate"
              checked={checks.invite}
              onPress={() => toggle('invite')}
              onCtaPress={() => toggle('invite')}
            />
          </VStack>
        </Card>
      </Section>

      <Section title="Header (frosted)">
        <View style={styles.navPreview}>
          <Header
            showTopInset={false}
            leading={
              <Pressable hitSlop={8} accessibilityRole="button" accessibilityLabel="Menu">
                <View style={styles.iconBtn}>
                  <View style={styles.menuLine} />
                  <View style={styles.menuLine} />
                  <View style={styles.menuLine} />
                </View>
              </Pressable>
            }
            trailing={
              <HStack spacing="sm" align="center">
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Search"
                  style={styles.iconBtn}
                >
                  <Search size={18} color="#8BA8B8" strokeWidth={1.8} />
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Notifications"
                  style={styles.iconBtn}
                >
                  <Bell size={18} color="#8BA8B8" strokeWidth={1.8} />
                </Pressable>
                <HeaderAvatar initials="JV" />
              </HStack>
            }
          />
        </View>
      </Section>

      <Section title="BottomNav (spring tabs)">
        <View style={styles.navPreview}>
          <BottomNav tabs={navTabs} activeKey={activeTab} />
        </View>
        <Text style={darkPrecisionTypography.bodyMuted}>
          Tap a tab to see the spring scale + indicator slide. Active: {activeTab}
        </Text>
      </Section>

      <Section title="Status banners">
        <VStack spacing="sm">
          <StatusBanner variant="info" message="Sync in progress · 12 leads queued" />
          <StatusBanner variant="warning" message="Voice agent quota at 80%" />
          <StatusBanner variant="danger" message="Connection lost · retrying in 5s" />
        </VStack>
      </Section>

      <Section title="Skeleton primitives">
        <VStack spacing="md">
          <ShimmerSkeleton width="60%" height={24} />
          <ShimmerSkeleton width="100%" height={16} />
          <SkeletonCard />
        </VStack>
      </Section>

      <Section title="Inputs & fields">
        <VStack spacing="md">
          <Input label="Full name" placeholder="e.g. Mariana Restrepo" />
          <Input
            label="Work email"
            placeholder="name@company.com"
            error="Enter a valid work email"
          />
          <SearchInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search leads, companies…"
          />
          <Select
            variant="dropdown"
            options={[
              { value: 'new', label: 'New' },
              { value: 'qualified', label: 'Qualified', color: colors.primary.main },
              { value: 'won', label: 'Won', color: colors.status.success.main },
              { value: 'lost', label: 'Lost', color: colors.status.error.main },
            ]}
            value={stage}
            onChange={setStage}
            placeholder="Select stage"
          />
        </VStack>
      </Section>

      <Section title="Typography (Dark Precision)">
        <VStack spacing="sm">
          <Text style={darkPrecisionTypography.display}>Display · Sora 800</Text>
          <Text style={darkPrecisionTypography.heading}>Heading · Sora 600</Text>
          <Text style={darkPrecisionTypography.body}>Body · Manrope 400 — the quick brown fox</Text>
          <Text style={darkPrecisionTypography.label}>Label · Manrope 500 uppercase</Text>
          <Text style={darkPrecisionTypography.metric}>42,180</Text>
          <Text style={darkPrecisionTypography.metricSm}>+8.4% vs last week</Text>
        </VStack>
      </Section>
    </ScrollView>
  );
}

function Section({ title, children }: Readonly<{ title: string; children: ReactNode }>) {
  return (
    <Box marginBottom="2xl">
      <Text style={[darkPrecisionTypography.label, styles.sectionTitle]}>{title}</Text>
      <View style={styles.sectionBody}>{children}</View>
    </Box>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    padding: tokens.spacing.xl,
    paddingBottom: tokens.spacing['4xl'],
  },
  eyebrow: {
    marginBottom: tokens.spacing.xs,
  },
  heading: {
    marginBottom: tokens.spacing['2xl'],
  },
  sectionTitle: {
    marginBottom: tokens.spacing.md,
  },
  sectionBody: {
    gap: tokens.spacing.md,
  },
  row: {
    flexWrap: 'wrap',
    rowGap: tokens.spacing.xs,
  },
  navPreview: {
    borderRadius: tokens.radius.lg,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.border.subtle,
    marginBottom: tokens.spacing.sm,
  },
  iconBtn: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    gap: 3,
  },
  menuLine: {
    width: 14,
    height: 1.5,
    backgroundColor: colors.text.secondary,
    borderRadius: 1,
  },
});
